import { parseApiDate } from '@/lib/utils'

// One date format for the whole app: "Sep 16, 2026" (and "Sep 16, 2026,
// 2:05 PM" with a time). "06/29/26" and "09-16-2026" are easy to misread —
// day/month order, two-digit years — and a misread received date or
// follow-up costs real money. Format at render only; never store, send, sort
// or export the display form.
//
// Month names are a fixed English table rather than toLocale*/Intl so the
// output can't drift with the browser's locale or ICU version (newer ICU puts
// a narrow no-break space before "PM", which breaks text matching).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_ONLY = /^(\d{4})-(\d{2})$/

/** Calendar parts of an API value, or null if it isn't a date.
 *
 *  'YYYY-MM-DD' is read straight off the string — `new Date('2026-09-16')` is
 *  UTC midnight and would show Sep 15 west of Greenwich. Anything with a time
 *  goes through parseApiDate and is shown in the reader's own zone, so a scan
 *  at 8pm in Brewster doesn't read as tomorrow. */
function parts(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const s = iso.trim()
  const m = DATE_ONLY.exec(s)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = parseApiDate(s)
  return Number.isNaN(d.getTime()) ? null : d
}

function time(d: Date): string {
  const h = d.getHours() % 12 || 12
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() < 12 ? 'AM' : 'PM'}`
}

/** 'Sep 16, 2026'. Takes 'YYYY-MM-DD' or a full timestamp. */
export function fmtDate(iso: string | null | undefined, fallback = ''): string {
  const d = parts(iso)
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : fallback
}

/** '2026-09-16' — CSV/xlsx export ONLY. Excel reads '09/16/26' by the
 *  machine's locale and sorts it wrong; ISO sorts as text and can't be
 *  misread. The day is the reader's local one, same as fmtDate on screen —
 *  slicing a UTC timestamp would export a 7 PM ET PO as the next day. */
export function fmtIsoDate(iso: string | null | undefined, fallback = ''): string {
  const d = parts(iso)
  if (!d) return fallback
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 'Sep 16, 2026, 2:05 PM' in the reader's local zone. */
export function fmtDateTime(iso: string | null | undefined, fallback = ''): string {
  const d = parts(iso)
  return d ? `${fmtDate(iso)}, ${time(d)}` : fallback
}

/** '2:05 PM' — for "as of" labels where the day is obvious. */
export function fmtTime(date: Date): string {
  return time(date)
}

/** '2:05:33 PM' — "Last scan" labels. Staff scanning several items a minute
 *  watch the seconds tick to confirm each scan registered. */
export function fmtTimeWithSeconds(date: Date): string {
  const [hm, ampm] = time(date).split(' ')
  return `${hm}:${String(date.getSeconds()).padStart(2, '0')} ${ampm}`
}

/** 'Sep 2026'. Takes a 'YYYY-MM' month bucket or any date fmtDate accepts. */
export function fmtMonth(iso: string | null | undefined, fallback = ''): string {
  const m = iso ? MONTH_ONLY.exec(iso.trim()) : null
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : parts(iso)
  return d ? `${MONTHS[d.getMonth()]} ${d.getFullYear()}` : fallback
}

/** 'Sep 16' — chart axis ticks ONLY, where the year would crowd the axis. The
 *  tooltip must still show the full fmtDate. */
export function fmtAxisDate(iso: string | null | undefined, fallback = ''): string {
  const d = parts(iso)
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}` : fallback
}

/** Today as 'YYYY-MM-DD' in the reader's local zone. `new Date().toISOString()
 *  .slice(0, 10)` is the UTC day, which rolls over to tomorrow around 8 PM
 *  Eastern — mid-show for an evening event. Compare against API date strings
 *  as plain strings; 'YYYY-MM-DD' sorts correctly as text. */
export function todayIso(): string {
  return fmtIsoDate(new Date().toISOString())
}

/** 'YYYY-MM-DD' shifted by whole days, computed on the local calendar so the
 *  result never drifts a day through a UTC conversion. */
export function addDaysIso(iso: string, days: number): string {
  const d = parts(iso)
  if (!d) return ''
  d.setDate(d.getDate() + days)
  return fmtIsoDate(d.toISOString())
}
